import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { ClassSession, MeetingProvider } from '@/lib/types'

const sessionsKey = (courseSlug: string) => ['course-sessions', courseSlug] as const
const managedKey = (courseId: string) => ['managed-sessions', courseId] as const

/**
 * useCourseSessions membaca jadwal sesi sebuah kelas.
 *
 * Dapat dipanggil pengunjung yang belum masuk: jadwalnya terbuka, sedangkan
 * tautan ruang rapat hanya disertakan peladen bagi yang berhak.
 */
export function useCourseSessions(slug: string) {
  return useQuery({
    queryKey: sessionsKey(slug),
    queryFn: () => api.get<{ sessions: ClassSession[] }>(`/courses/${slug}/sessions`),
    enabled: Boolean(slug),
    select: (data) => data.sessions,
  })
}

export function useManagedSessions(courseId: string) {
  return useQuery({
    queryKey: managedKey(courseId),
    queryFn: () => api.get<{ sessions: ClassSession[] }>(`/manage/courses/${courseId}/sessions`),
    enabled: Boolean(courseId),
    select: (data) => data.sessions,
  })
}

export interface SessionDraft {
  title: string
  description?: string
  starts_at: string
  ends_at: string
  provider?: MeetingProvider
  meeting_url?: string
  meeting_passcode?: string
  recording_url?: string
  status?: string
}

export function useSaveSession(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...draft }: SessionDraft & { id?: string }) =>
      id
        ? api.patch<{ session: ClassSession }>(`/manage/sessions/${id}`, draft)
        : api.post<{ session: ClassSession }>(`/manage/courses/${courseId}/sessions`, draft),
    onSuccess: async () => {
      // Jadwal tampil di dua tempat sekaligus — panel pengajar dan halaman
      // kelas — sehingga keduanya dimuat ulang, bukan hanya yang sedang
      // dilihat.
      await client.invalidateQueries({ queryKey: managedKey(courseId) })
      await client.invalidateQueries({ queryKey: ['course-sessions'] })
    },
  })
}

export function useDeleteSession(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) => api.delete<void>(`/manage/sessions/${sessionId}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: managedKey(courseId) })
      await client.invalidateQueries({ queryKey: ['course-sessions'] })
    },
  })
}
